<script setup lang="ts">
import type {
  ButtonBlock as ButtonBlockType,
  ButtonClickAction,
  ViewportSize,
} from "@aswin.dev/types";
import { computed, inject } from "vue";
import { EDITOR_KEY } from "../../keys";

const props = defineProps<{
  block: ButtonBlockType;
  viewport: ViewportSize;
}>();

/** Narrow inject type — ButtonBlock only needs canvas navigation in the editor. */
type EditorWithCanvasNav = {
  switchToNextCanvasPage?: () => boolean;
  switchToPreviousCanvasPage?: () => boolean;
};

const editor = inject(EDITOR_KEY, null) as EditorWithCanvasNav | null;

const clickAction = computed(
  (): ButtonClickAction => props.block.clickAction ?? "none",
);

const previewHref = computed(() => {
  const action = clickAction.value;
  if (action === "scroll_anchor") {
    const id = props.block.clickActionAnchorId?.trim().replace(/^#/, "") ?? "";
    return id ? `#${id}` : props.block.url || "#";
  }
  return props.block.url || "#";
});

const buttonStyle = computed(() => {
  const style: Record<string, string> = {
    display: "inline-block",
    boxSizing: "border-box",
    padding: `${props.block.buttonPadding.top}px ${props.block.buttonPadding.right}px ${props.block.buttonPadding.bottom}px ${props.block.buttonPadding.left}px`,
    backgroundColor: props.block.backgroundColor,
    color: props.block.textColor,
    fontSize: `${props.block.fontSize}px`,
    fontWeight: "bold",
    textDecoration: "none",
    borderRadius: `${props.block.borderRadius}px`,
    textAlign: "center",
  };
  if (props.block.fontFamily) {
    style.fontFamily = props.block.fontFamily;
  }

  const bw = props.block.borderWidth ?? 0;
  if (bw > 0) {
    const c = props.block.borderColor ?? "#000000";
    const side = props.block.borderSides ?? "all";
    if (side === "all") {
      style.border = `${bw}px solid ${c}`;
    } else {
      const z = "0px";
      style.borderStyle = "solid";
      style.borderColor = c;
      style.borderTopWidth = side === "top" ? `${bw}px` : z;
      style.borderRightWidth = side === "right" ? `${bw}px` : z;
      style.borderBottomWidth = side === "bottom" ? `${bw}px` : z;
      style.borderLeftWidth = side === "left" ? `${bw}px` : z;
    }
  }

  return style;
});

function handleButtonClick(e: MouseEvent): void {
  const action = clickAction.value;
  if (action === "next_step") {
    e.preventDefault();
    editor?.switchToNextCanvasPage?.();
    return;
  }
  if (action === "previous_step") {
    e.preventDefault();
    editor?.switchToPreviousCanvasPage?.();
    return;
  }
  if (action === "scroll_anchor" && props.block.clickActionAnchorId?.trim()) {
    e.preventDefault();
    const id = props.block.clickActionAnchorId.trim().replace(/^#/, "");
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    return;
  }
  if (action === "close_popup" || action === "load_page") {
    e.preventDefault();
    return;
  }
  e.preventDefault();
}
</script>

<template>
  <div class="tpl:text-center">
    <a
      :href="previewHref"
      :style="buttonStyle"
      :class="
        clickAction !== 'none' ? 'tpl:cursor-pointer' : 'tpl:cursor-default'
      "
      @click="handleButtonClick"
    >
      {{ block.text }}
    </a>
  </div>
</template>
